import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc,
  deleteDoc, doc, serverTimestamp, orderBy, query,
} from 'firebase/firestore';
import { db } from '../firebase';
import ConfirmDialog from './ConfirmDialog';
import { useToast } from './Toast';

// Used when the participantFields collection is empty / not yet seeded
const FALLBACK_FIELDS = [
  { key: 'firstName',             label: 'First name',                       type: 'text', required: true,  placeholder: '',                                        fullWidth: false },
  { key: 'lastName',              label: 'Last name',                        type: 'text', required: true,  placeholder: '',                                        fullWidth: false },
  { key: 'dateOfBirth',           label: 'Date of birth',                    type: 'date', required: true,  placeholder: '',                                        fullWidth: false },
  { key: 'passportNumber',        label: 'Passport / ID number',             type: 'text', required: false, placeholder: 'Optional',                                fullWidth: false },
  { key: 'dietaryRequirements',   label: 'Dietary requirements / allergies', type: 'text', required: false, placeholder: 'Optional — e.g. Vegetarian, Nut allergy', fullWidth: true  },
  { key: 'emergencyContactName',  label: 'Emergency contact name',           type: 'text', required: false, placeholder: 'Optional',                                fullWidth: false },
  { key: 'emergencyContactPhone', label: 'Emergency contact phone',          type: 'tel',  required: false, placeholder: 'Optional',                                fullWidth: false },
];

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function displayValue(field, participant) {
  const val = participant[field.key];
  if (!val) return '—';
  return field.type === 'date' ? formatDate(val) : val;
}

function ParticipantForm({ fields, initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(
    () => Object.fromEntries(fields.map((f) => [f.key, initial?.[f.key] ?? '']))
  );

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSave(form);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-surface rounded-[12px] border border-[#E5E7EB]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((field) => (
          <div key={field.key} className={field.fullWidth ? 'sm:col-span-2' : ''}>
            <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
              {field.label}{field.required && <span className="text-dr"> *</span>}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                name={field.key}
                value={form[field.key] ?? ''}
                onChange={handleChange}
                required={field.required}
                placeholder={field.placeholder || ''}
                rows={3}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
              />
            ) : (
              <input
                type={field.type}
                name={field.key}
                value={form[field.key] ?? ''}
                onChange={handleChange}
                required={field.required}
                placeholder={field.placeholder || ''}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save participant'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function ParticipantList({ requestId, expectedCount, editable = false }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState(FALLBACK_FIELDS);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'participantFields'), orderBy('order', 'asc')),
      (snap) => {
        const active = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((f) => f.active !== false);
        if (active.length > 0) setFields(active);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'requests', requestId, 'participants'));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() ?? a.submittedAt?.toMillis?.() ?? 0;
        const tb = b.createdAt?.toMillis?.() ?? b.submittedAt?.toMillis?.() ?? 0;
        return ta - tb;
      });
      setParticipants(docs);
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  async function handleAdd(form) {
    setSaving(true);
    try {
      await addDoc(collection(db, 'requests', requestId, 'participants'), {
        ...form,
        createdAt: serverTimestamp(),
      });
      setShowAddForm(false);
      toast?.('Participant saved.', 'success');
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(id, form) {
    setSaving(true);
    try {
      const update = {};
      fields.forEach((f) => { update[f.key] = form[f.key] ?? ''; });
      await updateDoc(doc(db, 'requests', requestId, 'participants', id), update);
      setEditingId(null);
      toast?.('Participant updated.', 'success');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'requests', requestId, 'participants', id));
      toast?.('Participant removed.', 'info');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }

  const registered = participants.length;
  const total = expectedCount || 0;
  const progress = total > 0 ? Math.min(Math.round((registered / total) * 100), 100) : 0;

  const nameFields = fields.filter((f) => f.key === 'firstName' || f.key === 'lastName');
  const otherColumns = fields.filter((f) => f.key !== 'firstName' && f.key !== 'lastName');

  // Staff read-only view
  if (!editable) {
    return (
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Participants</h3>
          <span className="text-[12px] text-[#9CA3AF]">{registered} registered</span>
        </div>
        {loading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : participants.length === 0 ? (
          <p className="text-[13px] text-[#9CA3AF]">No participants registered yet.</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#F3F4F6] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">
                    {nameFields.length > 0 && <th className="pb-3 pr-4">Name</th>}
                    {otherColumns.map((f) => (
                      <th key={f.key} className="pb-3 pr-4">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F4F6]">
                  {participants.map((p) => (
                    <tr key={p.id}>
                      {nameFields.length > 0 && (
                        <td className="py-3 pr-4 font-medium text-[#111827]">
                          {[p.firstName, p.lastName].filter(Boolean).join(' ') || '—'}
                        </td>
                      )}
                      {otherColumns.map((f) => (
                        <td key={f.key} className="py-3 pr-4 text-[#374151]">
                          {displayValue(f, p)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {participants.map((p, i) => (
                <div key={p.id} className="rounded-[10px] bg-surface border border-[#F3F4F6] px-4 py-3">
                  <p className="text-[13px] font-semibold text-[#111827] mb-1">
                    {i + 1}. {nameFields.length > 0
                      ? ([p.firstName, p.lastName].filter(Boolean).join(' ') || `Participant ${i + 1}`)
                      : `Participant ${i + 1}`}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {otherColumns.map((f) => {
                      const val = displayValue(f, p);
                      if (val === '—') return null;
                      return (
                        <div key={f.key}>
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.7px]">{f.label}</p>
                          <p className="text-[12px] text-[#374151]">{val}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Customer editable view
  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      <ConfirmDialog
        open={!!confirmDeleteId}
        title="Remove participant?"
        message="This will permanently delete this participant's details."
        confirmLabel="Remove"
        danger
        onConfirm={() => handleDelete(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
      />
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Participants</h3>
        <span className="text-[12px] text-[#9CA3AF]">{registered}{total > 0 ? ` of ${total}` : ''} registered</span>
      </div>

      {total > 0 && (
        <div className="w-full bg-[#E5E7EB] rounded-full h-1.5 mb-5">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${progress === 100 ? 'bg-sg' : 'bg-pb'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : (
        <div className="space-y-3">
          {participants.map((p, i) => (
            <div key={p.id}>
              {editingId === p.id ? (
                <ParticipantForm
                  fields={fields}
                  initial={Object.fromEntries(fields.map((f) => [f.key, p[f.key] ?? '']))}
                  onSave={(form) => handleEdit(p.id, form)}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                />
              ) : (
                <div className="flex items-start justify-between gap-3 p-4 rounded-[10px] bg-surface border border-[#F3F4F6]">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-[#111827]">
                      {i + 1}. {[p.firstName, p.lastName].filter(Boolean).join(' ') || `Participant ${i + 1}`}
                    </p>
                    {otherColumns.map((f) => {
                      const val = p[f.key];
                      if (!val) return null;
                      return (
                        <p key={f.key} className="text-[12px] text-[#6B7280] mt-0.5">
                          {f.label}: {f.type === 'date' ? formatDate(val) : val}
                        </p>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => setEditingId(p.id)}
                      className="text-[12px] text-pb hover:text-navy font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(p.id)}
                      disabled={deletingId === p.id}
                      className="text-[12px] text-dr/70 hover:text-dr font-medium disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {showAddForm ? (
            <ParticipantForm
              fields={fields}
              onSave={handleAdd}
              onCancel={() => setShowAddForm(false)}
              saving={saving}
            />
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-3 border-2 border-dashed border-[#E5E7EB] rounded-[10px] text-[13px] text-[#9CA3AF] hover:border-pb/40 hover:text-pb transition font-medium"
            >
              + Add participant
            </button>
          )}
        </div>
      )}
    </div>
  );
}
